import { PropertyType } from '~/client/graph/types.generated'

import type { IManagedResourceLinkDefinition } from './resourceLink/managedResourceLinks'
import { getManagedResourceLinkByProperty } from './resourceLink/managedResourceLinks'
import type {
  IResourceLinkConfiguration,
  ResourceLinkTargetType,
} from './resourceLink/targets'
import { getResourceLinkTargetType } from './resourceLink/targets'

const EDITABLE_PROPERTY_TYPES = [
  PropertyType.Text,
  PropertyType.Number,
  PropertyType.Select,
  PropertyType.Hierarchy,
  PropertyType.Checkbox,
  PropertyType.Datetime,
  PropertyType.User,
  PropertyType.Company,
  PropertyType.File,
] as const

type EditablePropertyType = (typeof EDITABLE_PROPERTY_TYPES)[number]

const isEditablePropertyType = (
  propertyType: unknown,
): propertyType is EditablePropertyType =>
  EDITABLE_PROPERTY_TYPES.some(candidate => candidate === propertyType)

/**
 * Minimal resource-property shape needed to determine its input behavior.
 */
export interface IResourcePropertyInputDefinition {
  readonly resourceTypeId?: string | null
  readonly name?: string | null
  readonly propertyType?: PropertyType | string | null
  readonly configuration?: IResourceLinkConfiguration | null
}

/**
 * The mutually exclusive ways the frontend can present a resource property.
 */
export type ResourcePropertyInput =
  | {
      readonly kind: 'editable-field'
      readonly propertyType: EditablePropertyType
    }
  | {
      readonly kind: 'editable-field'
      readonly propertyType: PropertyType.ResourceLink
      readonly targetType: ResourceLinkTargetType
    }
  | {
      readonly kind: 'managed-relation'
      readonly definition: IManagedResourceLinkDefinition
    }
  | { readonly kind: 'calculated-value' }
  | { readonly kind: 'unsupported' }

/**
 * Classifies a resource property by the input behavior supported by the frontend.
 */
export const classifyResourcePropertyInput = ({
  resourceTypeId,
  name,
  propertyType,
  configuration,
}: IResourcePropertyInputDefinition): ResourcePropertyInput => {
  if (isEditablePropertyType(propertyType)) {
    return { kind: 'editable-field', propertyType }
  }

  switch (propertyType) {
    case PropertyType.ResourceLink: {
      const targetType = getResourceLinkTargetType(configuration)
      if (targetType) {
        return { kind: 'editable-field', propertyType, targetType }
      }

      const definition = getManagedResourceLinkByProperty({
        resourceTypeId,
        name,
        propertyType,
        configuration,
      })
      return definition
        ? { kind: 'managed-relation', definition }
        : { kind: 'unsupported' }
    }
    case PropertyType.Rollup:
    case PropertyType.ComputedField:
      return { kind: 'calculated-value' }
    default:
      return { kind: 'unsupported' }
  }
}

/**
 * Counts visible template properties rendered as inputs during record creation.
 */
export const countVisibleResourceInputs = ({
  resourceTypeId,
  properties,
}: {
  readonly resourceTypeId?: string | null
  readonly properties?: ReadonlyArray<{
    readonly isHidden?: boolean | null
    readonly property?: IResourcePropertyInputDefinition | null
  }> | null
}): number =>
  (properties ?? []).filter(({ isHidden, property }) => {
    if (isHidden || !property) return false

    return (
      classifyResourcePropertyInput({
        resourceTypeId: property.resourceTypeId ?? resourceTypeId,
        name: property.name,
        propertyType: property.propertyType,
        configuration: property.configuration,
      }).kind === 'editable-field'
    )
  }).length
